// Only reorganize the server-authorized routes for display. Never change menu/action grants.
export interface MenuEntry {
    id?: string | number;
    n: string;
    u?: string;
    i?: string;
    l?: MenuEntry[];
}

export interface NavigationItem {
    key: string;
    n: string;
    u: string;
    tabs: MenuEntry[];
    tools: MenuEntry[];
    toolsLabel: string;
}

interface ItemSpec {
    n: string;
    tabs: string[];
    tools?: string[];
    toolsLabel?: string;
}

interface GroupSpec {
    key: string;
    n: string;
    icon: string;
    items: ItemSpec[];
}

export interface NavigationGroup {
    key: string;
    n: string;
    icon: string;
    l: NavigationItem[];
}

const titles: {[path: string]: string} = {
    '/home': '工作台', '/club-list': '商户列表', '/customer-followups': '跟进待办',
    '/customer-analytics': '经营概况', '/customer-classification': '商户分类',
    '/subscriptions': '订阅状态', '/vip-order': '开通续费记录', '/vip-type': 'VIP套餐',
    '/face-manage': '实名管理',
    '/customer-orders': '商户订单', '/customer-users': '商户用户',
    '/customer-fund-log': '资金记录', '/customer-cash': '提现记录',
    '/adsense-user': '认证审核', '/merchant-logout': '注销审核',
    '/adsense-page': '店铺产品', '/adsense-url': '店铺链接', '/adsense-kf': '店铺客服',
    '/adsense-log': '店铺操作日志', '/adsense-banner': '店铺轮播',
    '/category': '文章分类', '/esa-config': 'ESA参数',
};

const groups: GroupSpec[] = [
    {key: 'merchants', n: '商户管理', icon: 'el-icon-user', items: [
        {n: '商户列表', tabs: ['/club-list'], tools: ['/customer-classification', '/adsense-page', '/adsense-url', '/adsense-kf'], toolsLabel: '商户工具'},
        {n: '订阅管理', tabs: ['/subscriptions', '/vip-order'], tools: ['/vip-type', '/adsense-limit'], toolsLabel: '套餐设置'},
        {n: '业务查询', tabs: ['/customer-orders', '/customer-users', '/customer-phone', '/customer-fund-log', '/customer-cash'], tools: ['/customer-operation-log', '/adsense-log'], toolsLabel: '操作记录'},
        {n: '审核处理', tabs: ['/adsense-user', '/merchant-logout']},
        {n: '接入配置', tabs: ['/merchant-domain', '/merchant-pay-config', '/platform-pay-config', '/face-manage', '/esa-config']},
    ]},
    {key: 'service', n: '客服中心', icon: 'el-icon-service', items: [
        {n: '客服会话', tabs: ['/chatroom']},
        {n: '反馈处理', tabs: ['/newfeed', '/replyfeed', '/customer-feedback']},
    ]},
    {key: 'content', n: '内容管理', icon: 'el-icon-notebook-2', items: [
        {n: '教程与手册', tabs: ['/tutorials', '/merchant-manual']},
        {n: '帮助文章', tabs: ['/articles'], tools: ['/category'], toolsLabel: '分类设置'},
        {n: '素材管理', tabs: ['/gift-assets', '/adsense-banner-resource', '/adsense-icon', '/adsense-badge']},
        {n: '站点内容', tabs: ['/adsense-banner', '/app-banner', '/topicmanage']},
        {n: '消息通知', tabs: ['/message', '/helper', '/order'], tools: ['/send_mp_template', '/sms-template'], toolsLabel: '通知工具'},
    ]},
    {key: 'operations', n: '平台运营', icon: 'el-icon-data-analysis', items: [
        {n: '平台用户', tabs: ['/userlist', '/userAnalysis']},
        {n: '合作者中心', tabs: ['/partners']},
        {n: '内部办公', tabs: ['/work-log', '/account-list']},
    ]},
    {key: 'settings', n: '系统设置', icon: 'el-icon-setting', items: [
        {n: '员工权限', tabs: ['/staff', '/role', '/customerService'], tools: ['/menulist'], toolsLabel: '菜单设置'},
        {n: '系统配置', tabs: ['/admin-config', '/officialweb-config', '/customer-config', '/order-config'], tools: ['/officialaccount-config', '/application-config', '/minipro-config', '/sensitive'], toolsLabel: '更多配置'},
        {n: '操作日志', tabs: ['/loginMsg', '/userlog', '/stafflog', '/importantlog']},
        {n: '开发配置', tabs: ['/plugin-unit', '/apiAbnormal'], tools: ['/whitelist'], toolsLabel: '访问设置'},
    ]},
];

export function buildPlatformNavigation(menu: MenuEntry[]) {
    const routes = new Map<string, MenuEntry>();
    const collect = (items: MenuEntry[]) => items.forEach(item => {
        if (item.u && !routes.has(item.u)) routes.set(item.u, {...item, n: titles[item.u] || item.n});
        if (item.l) collect(item.l);
    });
    collect(menu || []);
    // The workbench has always been available to signed-in staff.
    routes.set('/home', {n: '工作台', u: '/home'});
    const assigned = new Set<string>();
    const take = (paths: string[]) => paths.filter(path => routes.has(path) && !assigned.has(path)).map(path => {
        assigned.add(path);
        return routes.get(path)!;
    });
    const itemFor = (spec: ItemSpec): NavigationItem => {
        const tabs = take(spec.tabs);
        const tools = take(spec.tools || []);
        const first = tabs[0] || tools[0];
        return {key: spec.tabs[0], n: spec.n, u: first ? first.u! : '', tabs, tools, toolsLabel: spec.toolsLabel || '更多'};
    };
    const home = itemFor({n: '工作台', tabs: ['/home', '/customer-followups', '/customer-analytics']});
    const result: NavigationGroup[] = groups.map(group => ({
        key: group.key, n: group.n, icon: group.icon,
        l: group.items.map(itemFor).filter(item => item.u),
    })).filter(group => group.l.length);

    // Keep future/custom authorized entries discoverable until they receive a dedicated grouping.
    const remaining: NavigationItem[] = [];
    routes.forEach((entry, path) => {
        if (!assigned.has(path)) remaining.push(itemFor({n: entry.n, tabs: [path]}));
    });
    if (remaining.length) result.push({key: 'other', n: '其他功能', icon: 'el-icon-folder', l: remaining});
    return {home, groups: result};
}

export function navigationMatches(item: NavigationItem, path: string, parentRoute?: string) {
    return [...item.tabs, ...item.tools].some(entry => entry.u === path || entry.u === parentRoute);
}

export function currentNavigation(menu: MenuEntry[], path: string, parentRoute?: string) {
    const navigation = buildPlatformNavigation(menu);
    return [navigation.home, ...navigation.groups.reduce<NavigationItem[]>((items, group) => items.concat(group.l), [])]
        .find(item => navigationMatches(item, path, parentRoute));
}
